import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn.js';

// ---------------------------------------------------------------------------
// Tooltip
//
// Lightweight hover/focus tooltip. The surface is rendered via a React
// portal to document.body and positioned with `position: fixed` against
// the trigger's bounding rect, so it survives ancestors that clip
// overflow (the Insights bucket panels are scroll containers - an
// `absolute` surface inside one would get sliced by the scrollbox).
//
// The trigger wrapper still receives pointer events even when its child
// is a natively `disabled` element (Button sets `pointer-events: none`
// on disabled) - the cursor enters the wrapper's bounding box and
// `onMouseEnter` fires on the parent span. Focus is mirrored via the
// wrapper's `onFocus` / `onBlur`, which React surfaces using the
// bubbling focusin / focusout events from any descendant.
//
// Pass plain string `content` for the common case; pass JSX via `content`
// when you need formatting. Render nothing if `content` is null/empty so
// callers can flip a tooltip on and off based on dynamic block reasons
// (e.g. "Enable" buttons that are only blocked when a check is missing).
// ---------------------------------------------------------------------------

export interface TooltipProps {
  content: ReactNode | null | undefined;
  children: ReactNode;
  // Where the tooltip floats relative to the trigger. Defaults to top.
  // `left` / `right` centre the surface vertically on the trigger so it
  // reads as a side-flyout - useful for collapsed sidebar icons that
  // would otherwise spill off the viewport with a top/bottom anchor.
  side?: 'top' | 'bottom' | 'left' | 'right';
  // Optional class merged onto the wrapper - mostly for inline-flex /
  // block sizing tweaks the consumer needs (e.g. matching the child's
  // intrinsic width).
  className?: string;
  // Delay (ms) between mouse-enter / focus and the surface appearing.
  // Cancelled if the pointer leaves before the timer fires. Defaults to
  // 0 (instant) to preserve existing call sites.
  delayMs?: number;
  // Drops `whitespace-nowrap` and adds a max-width so multi-line bodies
  // (rich title + description content) wrap instead of busting out of
  // the surface. Defaults to false.
  multiline?: boolean;
}

interface Coords {
  left: number;
  top: number;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  className,
  delayMs = 0,
  multiline = false,
}: TooltipProps): JSX.Element {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  // Held across renders so we can cancel a pending show when the
  // pointer leaves before the delay elapses.
  const showTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      return;
    }
    const node = triggerRef.current;
    if (node === null) return;

    const update = (): void => {
      const rect = node.getBoundingClientRect();
      const left =
        side === 'left'
          ? rect.left
          : side === 'right'
            ? rect.right
            : rect.left + rect.width / 2;
      const top =
        side === 'top'
          ? rect.top
          : side === 'bottom'
            ? rect.bottom
            : rect.top + rect.height / 2;
      setCoords({ left, top });
    };
    update();
    // `true` (capture phase) so scrolls in *any* ancestor reposition the
    // surface, including the Insights bucket scroll panels.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [visible, side]);

  // Clear any pending show-timer on unmount so a stale callback doesn't
  // flip visible after the trigger is gone.
  useEffect(() => () => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  if (content === null || content === undefined || content === '') {
    return <>{children}</>;
  }

  const show = (): void => {
    if (delayMs <= 0) {
      setVisible(true);
      return;
    }
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
    }
    showTimerRef.current = window.setTimeout(() => {
      showTimerRef.current = null;
      setVisible(true);
    }, delayMs);
  };
  const hide = (): void => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setVisible(false);
  };

  const transformClass =
    side === 'top'
      ? 'translate(-50%, calc(-100% - 4px))'
      : side === 'bottom'
        ? 'translate(-50%, 4px)'
        : side === 'left'
          ? 'translate(calc(-100% - 4px), -50%)'
          : 'translate(4px, -50%)';

  return (
    <>
      <span
        ref={triggerRef}
        className={cn('inline-flex', className)}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {visible && coords !== null
        ? createPortal(
            <span
              role="tooltip"
              style={{
                position: 'fixed',
                left: coords.left,
                top: coords.top,
                transform: transformClass,
              }}
              className={cn(
                'pointer-events-none z-[9999] rounded-md bg-surface-3 px-2 py-1 text-xs text-text shadow-md ring-1 ring-border',
                multiline ? 'max-w-xs whitespace-normal break-words' : 'whitespace-nowrap',
              )}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
